import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import Modal from '../UI/Modal';
import LoadingSpinner from '../UI/LoadingSpinner';
import { History, User, Calendar, Edit3, Trash2, Plus } from 'lucide-react';

export interface AuditLogEntry {
  _id: string;
  entityType: 'quotation' | 'boq';
  entityId: string;
  entityNumber: string;
  action: 'create' | 'update' | 'delete';
  performedBy: { _id: string; name: string; email?: string };
  performedAt: string;
  changes?: Array<{ field: string; oldValue?: any; newValue?: any }>;
  snapshot?: {
    number?: string;
    customer?: string;
    projectName?: string;
    projectAddress?: string;
    status?: string;
    date?: string;
  };
}

interface HistoryLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'quotation' | 'boq';
  entityId?: string | null;
  entityNumber?: string | null;
  title?: string;
}

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

const getUserDisplay = (entry: AuditLogEntry) => {
  const u = entry.performedBy;
  if (!u) return 'Unknown';
  return u.name ? (u.email ? `${u.name} (${u.email})` : u.name) : u.email || 'Unknown';
};

const HistoryLogModal: React.FC<HistoryLogModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId = null,
  entityNumber = null,
  title
}) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    const params: { entityType: 'quotation' | 'boq'; entityId?: string } = { entityType };
    if (entityId) params.entityId = entityId;
    apiService
      .getAuditLogs(params)
      .then((res) => setLogs(Array.isArray(res.data) ? res.data : []))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [isOpen, entityType, entityId]);

  const displayTitle =
    title ||
    (entityId && entityNumber
      ? `History: ${entityNumber}`
      : entityType === 'quotation'
      ? 'Quotations history'
      : 'BOQs history');

  const renderEntry = (entry: AuditLogEntry) => {
    const who = getUserDisplay(entry);
    const when = formatDateTime(entry.performedAt);
    const isCreate = entry.action === 'create';
    const isUpdate = entry.action === 'update';
    const isDelete = entry.action === 'delete';
    return (
      <div
        key={entry._id}
        className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50/50 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              isCreate ? 'bg-green-100 text-green-700' : isUpdate ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {isCreate && <Plus className="w-5 h-5" />}
            {isUpdate && <Edit3 className="w-5 h-5" />}
            {isDelete && <Trash2 className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {!entityId && (
                <span className="font-semibold text-gray-900">{entry.entityNumber}</span>
              )}
              <span className="text-gray-600">
                {isCreate && 'Created'}
                {isUpdate && 'Updated'}
                {isDelete && 'Deleted'}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4 flex-shrink-0" />
                {who}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4 flex-shrink-0" />
                {when}
              </span>
            </div>
            {isUpdate && entry.changes && entry.changes.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">What was edited:</p>
                <ul className="text-sm text-gray-700 space-y-0.5">
                  {entry.changes.map((c, i) => (
                    <li key={i}>
                      <span className="font-medium">{c.field}</span>
                      {c.oldValue !== undefined && c.newValue !== undefined && (
                        <span className="text-gray-600">
                          {' '}
                          → from <span className="font-mono text-xs">{String(c.oldValue)}</span> to{' '}
                          <span className="font-mono text-xs">{String(c.newValue)}</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {isDelete && entry.snapshot && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">Sample of deleted item:</p>
                <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded p-2 space-y-0.5">
                  {entry.snapshot.number && <p><strong>Number:</strong> {entry.snapshot.number}</p>}
                  {entry.snapshot.customer && <p><strong>Customer:</strong> {entry.snapshot.customer}</p>}
                  {entry.snapshot.projectName && <p><strong>Project:</strong> {entry.snapshot.projectName}</p>}
                  {entry.snapshot.projectAddress && <p><strong>Address:</strong> {entry.snapshot.projectAddress}</p>}
                  {entry.snapshot.status && <p><strong>Status:</strong> {entry.snapshot.status}</p>}
                  {entry.snapshot.date && <p><strong>Date:</strong> {new Date(entry.snapshot.date).toLocaleDateString()}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={displayTitle}
      size="xl"
      showCloseButton
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <p className="text-red-600 py-4">{error}</p>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-gray-500">
          <History className="w-12 h-12 mb-2" />
          <p>No history recorded yet.</p>
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto pr-1">{logs.map(renderEntry)}</div>
      )}
    </Modal>
  );
};

export default HistoryLogModal;
